import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { ArrowLeft, Scale, Info } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{title}</h2>
    <div className="text-zinc-400 text-sm md:text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Terms() {
  const navigate = useNavigate();
  const { t, language } = useI18n();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-zinc-400" data-testid="terms-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('back') || 'Back'}
          </Button>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Scale className="w-4 h-4 text-[#06B6D4]" />
            {t('lastUpdated') || 'Last updated: Jan 2026'}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">{t('termsTitle') || 'Terms of Service'}</h1>
        {language !== 'en' && t('legalTranslationsNote') && (
          <div className="inline-flex items-start gap-2 mb-6 px-3 py-2 rounded-lg bg-[#06B6D4]/5 border border-[#06B6D4]/20 text-xs text-zinc-400">
            <Info className="w-3.5 h-3.5 text-[#06B6D4] mt-0.5 shrink-0" />
            <span>{t('legalTranslationsNote')}</span>
          </div>
        )}
        <p className="text-zinc-500 mb-12">
          Welcome to <strong className="text-white">PlayMold</strong>. By creating an account or using the service you agree to these Terms.
        </p>

        <Section title="1. The Service">
          <p>PlayMold provides browser-based tools to design, generate, publish, and play games with AI assistance.</p>
        </Section>

        <Section title="2. Your Account">
          <p>You are responsible for your account credentials. Do not share them. You must be at least 13 years old.</p>
        </Section>

        <Section title="3. Your Content">
          <p>You retain ownership of the games and assets you create. By publishing a game to the public Gallery you grant PlayMold a non-exclusive,
          worldwide license to host and display it. You can unpublish at any time.</p>
        </Section>

        <Section title="4. Acceptable Use">
          <ul className="list-disc pl-6 space-y-1.5">
            <li>No illegal, harmful, hateful, or sexually explicit content.</li>
            <li>No malware, phishing, or attempts to disrupt the service.</li>
            <li>No content that infringes intellectual-property rights.</li>
            <li>No content targeting or intended to harm minors.</li>
          </ul>
          <p>We may remove content or terminate accounts that violate these rules.</p>
        </Section>

        <Section title="5. Subscriptions & Payments">
          <p>Paid tiers renew automatically until you cancel. Cancel any time in Settings; access continues until the end of the paid period.
          Refunds follow applicable consumer-protection laws. Taxes may be added at checkout.</p>
        </Section>

        <Section title="6. AI-Generated Output">
          <p>Output from AI features is provided "as is". You are responsible for how you use it. We do not guarantee originality or
          non-infringement of any specific output.</p>
        </Section>

        <Section title="7. Disclaimers">
          <p>The service is provided on an "as available" basis, without warranties of any kind. We do not guarantee uninterrupted
          availability. Beta features may change or be removed.</p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>To the maximum extent permitted by law, PlayMold's total liability for any claim is limited to the amounts you paid us in the
          12 months preceding the claim.</p>
        </Section>

        <Section title="9. Termination">
          <p>You may terminate your account any time. We may suspend or terminate accounts for policy violations or non-payment.</p>
        </Section>

        <Section title="10. Changes">
          <p>We may update these Terms. Material changes will be surfaced in-app. Continued use after changes means acceptance.</p>
        </Section>

        <Section title="11. Contact">
          <p>Questions? <a className="text-[#06B6D4] hover:underline" href="mailto:legal@playmold.app">legal@playmold.app</a>.</p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
